// src/pages/Reports/IssuanceReporting/AgentPrintHouseSummary/PrintHouseSummaryTab.tsx
// RPT-013 (README.md mục 7) — theo dõi tình hình sản xuất/bàn giao của TỪNG nhà in, có thể qua
// nhiều kỳ/lô giao khác nhau.

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';

import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { getPrintHouseSummaryReportApi } from 'helpers/fakebackend_helper';
import { ISSUANCE_REPORTS_BASE } from 'helpers/url_helper';
import IssuancePlanSelect from '../shared/IssuancePlanSelect';
import PrintHouseSelect from '../shared/PrintHouseSelect';
import { cleanParams, formatDateTime, formatQty, formatCurrency, getPaginatedPayload } from '../shared/formatters';
import { downloadReportExport } from '../shared/exportReport';
import { INITIAL_REPORT_PAGINATION, PrintHouseSummaryReportRow, ReportPaginationState } from '../shared/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

const PrintHouseSummaryTab: React.FC = () => {
  const [printHouseId, setPrintHouseId] = useState('');
  const [planId, setPlanId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'Xlsx' | 'Csv' | null>(null);
  const [rows, setRows] = useState<PrintHouseSummaryReportRow[]>([]);
  const [pagination, setPagination] = useState<ReportPaginationState>(INITIAL_REPORT_PAGINATION);

  const buildFilterParams = useCallback(
    () => ({
      PrintHouseId: printHouseId || undefined,
      IssuancePlanId: planId || undefined,
      FromDate: fromDate || undefined,
      ToDate: toDate || undefined,
    }),
    [printHouseId, planId, fromDate, toDate],
  );

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPrintHouseSummaryReportApi(
        cleanParams({ ...buildFilterParams(), PageIndex: pagination.currentPage, PageSize: pagination.pageSize }),
      );
      const { items, totalItems, totalPages } = getPaginatedPayload(res);
      setRows(items);
      setPagination((prev) => ({ ...prev, totalItems, totalPages }));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_bao_cao_theo_message'));
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, printHouseId, planId, fromDate, toDate]);

  const resetPage = () => setPagination((prev) => ({ ...prev, currentPage: 1 }));

  const handleExport = async (format: 'Xlsx' | 'Csv') => {
    setExporting(format);
    try {
      await downloadReportExport(`${ISSUANCE_REPORTS_BASE}/PrintHouseSummary`, buildFilterParams(), format, 'TheoNhaIn');
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_xuat_bao_cao_message'));
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
      <Row className="g-3 mb-3 align-items-end">
        <Col md={3}>
          <Label className="form-label">{st('nha_in_label')}</Label>
          <PrintHouseSelect value={printHouseId} onChange={(v) => { setPrintHouseId(v); resetPage(); }} />
        </Col>
        <Col md={3}>
          <Label className="form-label">{st('ky_phat_hanh_label')}</Label>
          <IssuancePlanSelect value={planId} onChange={(v) => { setPlanId(v); resetPage(); }} />
        </Col>
        <Col md={3}>
          <Label className="form-label">{st('tu_ngay_label')}</Label>
          <Input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); resetPage(); }} />
        </Col>
        <Col md={3}>
          <Label className="form-label">{st('den_ngay_label')}</Label>
          <Input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); resetPage(); }} />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col className="d-flex gap-2 justify-content-end">
          <Button color="success" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Xlsx')}>
            {exporting === 'Xlsx' ? <Spinner size="sm" /> : <i className="ri-file-excel-2-line align-bottom me-1"></i>}
            {st('excel_button')}
          </Button>
          <Button color="secondary" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Csv')}>
            {exporting === 'Csv' ? <Spinner size="sm" /> : <i className="ri-file-text-line align-bottom me-1"></i>}
            {st('csv_button')}
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : (
        <>
          <DataTableShell
            headerRow={
              <tr>
                <th className="text-center" style={{ width: 50 }}>{st('stt_column')}</th>
                <th>{st('nha_in_label')}</th>
                <th>{st('ky_phat_hanh_label')}</th>
                <th>{st('loai_ve_column')}</th>
                <th className="text-end">{st('dat_in_column')}</th>
                <th className="text-end">{st('thuc_nhan_column')}</th>
                <th className="text-end">{st('thieu_column')}</th>
                <th className="text-end">{st('loi_column')}</th>
                <th className="text-end">{st('gia_tri_column')}</th>
                <th>{st('ngay_hoan_thanh_column')}</th>
                <th>{st('trang_thai_column')}</th>
              </tr>
            }
          >
            {rows.length === 0 ? (
              <tr><td colSpan={11} className="text-center text-muted py-5">{st('khong_co_du_lieu_text')}</td></tr>
            ) : (
              rows.map((row, index) => (
                <tr key={`${row.printHouseId}-${row.issuancePlanId}`}>
                  <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
                  <td>{row.printHouseName}</td>
                  <td>{row.planCode}</td>
                  <td>{row.ticketTypeName || row.ticketType}</td>
                  <td className="text-end">{formatQty(row.expectedQuantity)}</td>
                  <td className="text-end">{formatQty(row.actualReceivedQuantity)}</td>
                  <td className="text-end text-danger">{formatQty(row.shortageQuantity)}</td>
                  <td className="text-end">{formatQty(row.errorCount)}</td>
                  <td className="text-end">{formatCurrency(row.value)}</td>
                  <td>{formatDateTime(row.completedAt)}</td>
                  <td>
                    <span className={`badge bg-${row.status === 'DaNhan' ? 'success' : 'warning'}-subtle text-${row.status === 'DaNhan' ? 'success' : 'warning'}`}>
                      {row.statusName}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </DataTableShell>

          <AgentPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
          />
        </>
      )}
    </>
  );
};

export default PrintHouseSummaryTab;
